import { useState, useEffect } from "react";
import { toast } from "sonner";
import BackToDashboard from "@/components/BackToDashboard";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Truck, Check, ShieldCheck, ExternalLink, Unlink, Copy, Package, Globe2 } from "lucide-react";

/**
 * CourierInfo.jsx
 * ------------------------------------------------------------------
 * Courier connection settings — available on every plan (Free through
 * Pro). Loads real data from GET /courier on mount, saves via POST
 * /courier. Once connected, orders are auto-shipped by either
 * "CheckoutDM - Shiprocket - Create Shipment" or "CheckoutDM -
 * Shippo - Create Shipment" right after payment, depending on which
 * provider is active — nothing on this page triggers a shipment itself.
 *
 * Only ONE provider can be active at a time. Saving either form
 * switches the active provider and clears the other one's stored
 * credentials automatically (enforced server-side) — no separate
 * "disconnect first" step needed.
 *
 * The Shiprocket password and Shippo API key fields are intentionally
 * write-only: GET /courier never returns them, only booleans (e.g.
 * shiprocket_connected, shippo_connected). Leaving either blank on
 * save keeps whatever's already stored, same as the Razorpay/Stripe/
 * PayPal secret fields on ProfileInfo.
 * ------------------------------------------------------------------
 */

const COUNTRIES = [
  ["US", "United States"], ["GB", "United Kingdom"], ["CA", "Canada"],
  ["AU", "Australia"], ["DE", "Germany"], ["FR", "France"], ["IT", "Italy"],
  ["ES", "Spain"], ["NL", "Netherlands"], ["IE", "Ireland"], ["NZ", "New Zealand"],
  ["SG", "Singapore"], ["AE", "United Arab Emirates"], ["IN", "India"],
];

// Catches the most common typo class before it ever reaches Shippo/USPS/UPS --
// e.g. an extra/missing digit on a US ZIP. Countries not listed here fall
// through as "valid" (still covered by the plain required-field check).
const POSTAL_PATTERNS = {
  US: /^\d{5}(-\d{4})?$/,
  CA: /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/,
  GB: /^[A-Za-z]{1,2}\d[A-Za-z\d]? ?\d[A-Za-z]{2}$/,
  AU: /^\d{4}$/,
  IN: /^\d{6}$/,
};

function isValidPostalCode(zip, country) {
  const pattern = POSTAL_PATTERNS[country];
  if (!pattern) return true;
  return pattern.test(String(zip).trim());
}

export default function CourierInfo() {
  const [loading, setLoading] = useState(true);
  const [activeProvider, setActiveProvider] = useState(null); // what's actually saved: null | 'shiprocket' | 'shippo'
  const [formProvider, setFormProvider] = useState("shiprocket"); // which form the merchant is looking at right now
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [credError, setCredError] = useState("");
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Shiprocket fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pickupLocation, setPickupLocation] = useState("");
  const [shiprocketConnected, setShiprocketConnected] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [webhookUrl, setWebhookUrl] = useState(null);
  const [webhookToken, setWebhookToken] = useState(null);
  const [shippoWebhookUrl, setShippoWebhookUrl] = useState(null);

  // Shippo fields
  const [apiKey, setApiKey] = useState("");
  const [shippoConnected, setShippoConnected] = useState(false);
  const [zipError, setZipError] = useState("");
  const [fromAddr, setFromAddr] = useState({
  name: "", company: "", street1: "", street2: "",
  city: "", state: "", zip: "", country: "US", phone: "", email: "",
});

  useEffect(() => {
    api
      .get("/courier")
      .then(({ data }) => {
        setEmail(data.shiprocket_email || "");
        setPickupLocation(data.shiprocket_pickup_location || "");
        setShiprocketConnected(!!data.shiprocket_connected);
        setShippoConnected(!!data.shippo_connected);
        setActiveProvider(data.courier_provider || null);
        setFormProvider(data.courier_provider || "shiprocket");
        setWebhookUrl(data.shiprocket_status_webhook_url || null);
        setWebhookToken(data.shiprocket_webhook_token || null);
        setShippoWebhookUrl(data.shippo_status_webhook_url || null);
        if (data.shippo_from_address) {
          setFromAddr((prev) => ({ ...prev, ...data.shippo_from_address }));
        }
      })
      .catch(() => toast.error("Couldn't load your courier settings. Try refreshing."))
      .finally(() => setLoading(false));
  }, []);

  const switchingProvider = activeProvider && activeProvider !== formProvider;

  const submitShiprocket = async () => {
    if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setEmailError("Enter a valid Shiprocket account email.");
      toast.error("Please fix the email before saving.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/courier", {
        provider: "shiprocket",
        shiprocket_email: email,
        shiprocket_password: password, // blank = leave existing password untouched
        shiprocket_pickup_location: pickupLocation,
      });
      setPassword("");
      setSaved(true);
      setCredError("");
      setActiveProvider("shiprocket");
      setShiprocketConnected(true);
      setShippoConnected(false);
      loadCourierSettings();
      toast.success("Courier settings saved.");
      setTimeout(() => setSaved(false), 2600);
    } catch (err) {
      if (err?.response?.data?.error === "invalid_shiprocket_credentials") {
        setCredError(err.response.data.message || "Shiprocket rejected this email/password combination.");
        toast.error("Shiprocket didn't accept those credentials.");
      } else {
        toast.error("Couldn't save your courier settings. Try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const submitShippo = async () => {
    const required = ["name", "street1", "city", "zip", "country", "phone", "email"];
    const missing = required.filter((f) => !fromAddr[f]);
    if (missing.length) {
      toast.error("Fill in the required ship-from address fields first.");
      return;
    }
    if (!isValidPostalCode(fromAddr.zip, fromAddr.country)) {
      const countryName = COUNTRIES.find(([code]) => code === fromAddr.country)?.[1] || fromAddr.country;
      setZipError(`That doesn't look like a valid postal code for ${countryName}.`);
      toast.error("Fix the ship-from postal code before saving.");
      return;
    }
    if (fromAddr.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(fromAddr.email)) {
      toast.error("Enter a valid ship-from email.");
      return;
    }
    if (!apiKey && !shippoConnected) {
      toast.error("Enter your Shippo API key.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/courier", {
        provider: "shippo",
        shippo_api_key: apiKey, // blank = leave existing key untouched
        shippo_from_address: fromAddr,
      });
      setApiKey("");
      setSaved(true);
      setCredError("");
      setActiveProvider("shippo");
      setShippoConnected(true);
      setShiprocketConnected(false);
      toast.success("Courier settings saved.");
      setTimeout(() => setSaved(false), 2600);
    } catch (err) {
      if (err?.response?.data?.error === "invalid_shippo_credentials") {
        setCredError(err.response.data.message || "Shippo rejected this API key.");
        toast.error("Shippo didn't accept that API key.");
      } else {
        toast.error("Couldn't save your courier settings. Try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const submit = (evt) => {
    evt.preventDefault();
    if (formProvider === "shiprocket") submitShiprocket();
    else submitShippo();
  };

  const disconnect = async () => {
    if (disconnecting || !activeProvider) return;
    setDisconnecting(true);
    try {
      await api.post("/courier/disconnect", { provider: activeProvider });
      toast.success(
        activeProvider === "shiprocket" ? "Shiprocket disconnected." : "Shippo disconnected."
      );
      setActiveProvider(null);
      setShiprocketConnected(false);
      setShippoConnected(false);
      setEmail("");
      setPassword("");
      setPickupLocation("");
      setApiKey("");
      setShowDisconnectConfirm(false);
    } catch (err) {
      toast.error("Couldn't disconnect — try again.");
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <section className="min-h-screen bg-cream px-6 py-12 md:px-14 md:py-16">
        <div className="mx-auto max-w-4xl">
          <BackToDashboard />
          <div className="mt-10 grid h-40 place-items-center text-sm text-ink/50">Loading…</div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-cream px-6 py-12 md:px-14 md:py-16">
      <div className="mx-auto max-w-4xl">
        <BackToDashboard />
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-hotpink">Courier</p>
        <h1 className="mt-3 text-4xl font-black tracking-tight text-ink md:text-6xl">
          Auto-ship every order.
        </h1>
        <p className="mt-4 max-w-xl text-ink/60">
          Connect a courier partner, and once you've packed an order you'll be able to click
          "Ship the Order" to generate the AWB, assign a courier, and schedule pickup —
          no manual entry needed. This is entirely optional; orders ship exactly as they
          do today if you skip this. Only one provider can be active at a time.
        </p>

        {activeProvider && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
            <span className="flex items-center gap-2">
              <ShieldCheck size={16} />
              Connected — new paid orders will auto-ship via{" "}
              {activeProvider === "shiprocket" ? "Shiprocket" : "Shippo"}.
            </span>
            <button
              type="button"
              onClick={() => setShowDisconnectConfirm(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-white px-3 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-50"
              data-testid="disconnect-courier"
            >
              <Unlink size={13} /> Disconnect
            </button>
          </div>
        )}

        {showDisconnectConfirm && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4">
            <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center">
              <p className="font-display text-xl font-black tracking-tight">
                Disconnect {activeProvider === "shiprocket" ? "Shiprocket" : "Shippo"}?
              </p>
              <p className="mt-2 text-sm text-ink/60">
                Orders will stop auto-shipping. You can reconnect anytime. This won't affect
                orders already shipped.
              </p>
              <div className="mt-5 flex gap-3">
                <button
                  onClick={() => setShowDisconnectConfirm(false)}
                  disabled={disconnecting}
                  className="flex-1 rounded-full border border-ink/15 py-2.5 text-sm font-semibold disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  onClick={disconnect}
                  disabled={disconnecting}
                  className="flex-1 rounded-full bg-hotpink py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {disconnecting ? "Please wait…" : "Yes, disconnect"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Provider picker */}
        <div className="mt-8 grid grid-cols-2 gap-3">
          <ProviderTab
            active={formProvider === "shiprocket"}
            connected={activeProvider === "shiprocket"}
            icon={<Package size={16} />}
            label="Shiprocket"
            sub="India-based shipping"
            onClick={() => setFormProvider("shiprocket")}
          />
          <ProviderTab
            active={formProvider === "shippo"}
            connected={activeProvider === "shippo"}
            icon={<Globe2 size={16} />}
            label="Shippo"
            sub="US, Europe, Australia & more"
            onClick={() => setFormProvider("shippo")}
          />
        </div>

        {switchingProvider && (
          <p className="mt-3 text-xs font-semibold text-hotpink">
            Saving this will disconnect {activeProvider === "shiprocket" ? "Shiprocket" : "Shippo"}{" "}
            and switch to {formProvider === "shiprocket" ? "Shiprocket" : "Shippo"}.
          </p>
        )}

        {formProvider === "shiprocket" && shiprocketConnected && webhookUrl && (
          <div className="mt-6 rounded-2xl border border-ink/10 bg-white p-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">
              Delivery status tracking
            </p>
            <p className="mt-1 text-sm text-ink/60">
              Add these three to your Shiprocket account to get automatic Delivered / RTO / Failed
              updates on your Orders tab instead of a static "Shipped" — no manual checking needed.
            </p>

            <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-ink/50">URL</p>
            <div className="mt-1.5 flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={webhookUrl}
                onFocus={(e) => e.target.select()}
                data-testid="shiprocket-status-webhook-url"
                className="min-w-0 flex-1 rounded-full border border-ink/15 bg-ink/[0.03] px-4 py-2 font-mono text-xs text-ink/70"
              />
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(webhookUrl);
                  toast.success("Copied — paste it into Shiprocket's URL field.");
                }}
                className="shrink-0 rounded-full bg-ink px-4 py-2 text-xs font-semibold text-cream hover:bg-ink/85"
              >
                <Copy size={12} className="mr-1 inline" /> Copy
              </button>
            </div>

            <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-ink/50">
              Auth Token Type — select <span className="font-mono normal-case">x-api-key</span>
            </p>

            {webhookToken && (
              <>
                <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-ink/50">Token</p>
                <div className="mt-1.5 flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={webhookToken}
                    onFocus={(e) => e.target.select()}
                    data-testid="shiprocket-status-webhook-token"
                    className="min-w-0 flex-1 rounded-full border border-ink/15 bg-ink/[0.03] px-4 py-2 font-mono text-xs text-ink/70"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(webhookToken);
                      toast.success("Copied — paste it into Shiprocket's Token field.");
                    }}
                    className="shrink-0 rounded-full bg-ink px-4 py-2 text-xs font-semibold text-cream hover:bg-ink/85"
                  >
                    <Copy size={12} className="mr-1 inline" /> Copy
                  </button>
                </div>
              </>
            )}

            <Link
              to="/shiprocket-tracking-guide"
              className="mt-3 inline-block text-xs font-semibold text-hotpink hover:underline"
            >
              How do I add this to Shiprocket? →
            </Link>
          </div>
        )}

        {formProvider === "shippo" && shippoConnected && shippoWebhookUrl && (
          <div className="mt-6 rounded-2xl border border-ink/10 bg-white p-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">
              Delivery status tracking
            </p>
            <p className="mt-1 text-sm text-ink/60">
              Add this to your Shippo account to get automatic Delivered / Returned / Failed
              updates on your Orders tab instead of a static "Shipped" — no manual checking needed.
            </p>

            <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-ink/50">
              Webhook URL — paste this into Shippo's Webhooks URL field
            </p>
            <div className="mt-1.5 flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={shippoWebhookUrl}
                onFocus={(e) => e.target.select()}
                data-testid="shippo-status-webhook-url"
                className="min-w-0 flex-1 rounded-full border border-ink/15 bg-ink/[0.03] px-4 py-2 font-mono text-xs text-ink/70"
              />
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(shippoWebhookUrl);
                  toast.success("Copied — paste it into Shippo's webhook URL field.");
                }}
                className="shrink-0 rounded-full bg-ink px-4 py-2 text-xs font-semibold text-cream hover:bg-ink/85"
              >
                <Copy size={12} className="mr-1 inline" /> Copy
              </button>
            </div>

            <p className="mt-4 text-xs text-ink/50">
              In Shippo, go to Settings → API → Webhooks — Add webhook. Set Event Type to{" "}
              <span className="font-semibold text-ink">Track Updated</span>, set Mode to{" "}
              <span className="font-semibold text-ink">Test</span> or{" "}
              <span className="font-semibold text-ink">Live</span> to match whichever API key
              you saved above, and paste the URL you just copied.
            </p>

            <Link
              to="/shippo-tracking-guide"
              className="mt-3 inline-block text-xs font-semibold text-hotpink hover:underline"
            >
              How do I add this to Shippo? →
            </Link>
          </div>
        )}

        <form onSubmit={submit} className="mt-8 space-y-8">
          {formProvider === "shiprocket" ? (
            <>
              <Section
                title="Shiprocket account"
                caption="Use the email and password you log into Shiprocket with. Signed up to Shiprocket with Google or your phone number? Use Shiprocket's 'Forgot password' link to set a password for your account first — CheckoutDM can only connect using email + password. Don't have an account? Shiprocket has a free tier — sign up at shiprocket.in first, then come back here."
              >
                <Field label="Shiprocket email">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) setEmailError("");
                      if (saved) setSaved(false);
                    }}
                    placeholder="you@yourbusiness.com"
                    className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-hotpink"
                    data-testid="shiprocket-email-input"
                  />
                  {emailError && <span className="mt-1 block text-xs font-semibold text-hotpink">{emailError}</span>}
                </Field>

                <Field
                  label="Shiprocket password"
                  hint={shiprocketConnected ? "leave blank to keep current password" : undefined}
                >
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (saved) setSaved(false);
                      if (credError) setCredError("");
                    }}
                    placeholder={shiprocketConnected ? "••••••••" : "Your Shiprocket password"}
                    className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-hotpink"
                    data-testid="shiprocket-password-input"
                    autoComplete="new-password"
                  />
                  {credError && <span className="mt-1 block text-xs font-semibold text-hotpink">{credError}</span>}
                </Field>
              </Section>

              <Section
                title="One-time setup: whitelist our server"
                caption="Shiprocket blocks API requests from servers it doesn't recognize. Before auto-shipping will work, add CheckoutDM's server IP to your Shiprocket account's allowed IPs — a one-time step."
              >
                <div className="rounded-2xl border border-ink/10 bg-ink/[0.02] p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-ink/70">CheckoutDM server IP</p>
                  <p className="mt-1 select-all font-mono text-lg font-bold text-ink">200.97.168.197</p>
                  <ol className="mt-3 list-decimal space-y-1 pl-4 text-sm text-ink/60">
                    <li>Log in to Shiprocket and go to Settings → API → Configure (or go directly to app.shiprocket.in/api-user).</li>
                    <li>Open your API user (the one you used above) and look for an IP restriction / whitelist option.</li>
                    <li>Add the IP above and save.</li>
                    <li>You need to add the IP while generating the new API user. If you edit the already created API user, it may not work.</li>
                  </ol>
                  <a
                    href="https://app.shiprocket.in/api-user"
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-hotpink hover:underline"
                  >
                    Open Shiprocket API settings <ExternalLink size={12} />
                  </a>
                </div>
              </Section>

              <Section
                title="Pickup location"
                caption="Enter the exact pickup location name as registered in your Shiprocket account (Settings → Pickup Addresses). This is where couriers collect your packages from."
              >
                <Field label="Pickup location name">
                  <input
                    value={pickupLocation}
                    onChange={(e) => {
                      setPickupLocation(e.target.value);
                      if (saved) setSaved(false);
                    }}
                    placeholder="e.g. Home, Warehouse"
                    className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-hotpink"
                    data-testid="shiprocket-pickup-location-input"
                  />
                </Field>
                <a
                  href="https://app.shiprocket.in/seller/settings/company-setup/pickup-addresses"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-hotpink hover:underline"
                >
                  Open Shiprocket pickup address settings <ExternalLink size={12} />
                </a>
              </Section>
            </>
          ) : (
            <>
              <Section
                title="Shippo API key"
                caption="Find this in your Shippo dashboard under API keys. Use a Test token first to try things out with no real charges, then switch to your Live token once you're ready to ship for real. Don't have an account? Sign up free at goshippo.com."
              >
                <Field
                  label="Shippo API key"
                  hint={shippoConnected ? "leave blank to keep current key" : undefined}
                >
                  <input
                    type="password"
                    value={apiKey}
                    onChange={(e) => {
                      setApiKey(e.target.value);
                      if (saved) setSaved(false);
                      if (credError) setCredError("");
                    }}
                    placeholder={shippoConnected ? "••••••••" : "shippo_test_... or shippo_live_..."}
                    className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-hotpink"
                    data-testid="shippo-api-key-input"
                    autoComplete="new-password"
                  />
                  {credError && <span className="mt-1 block text-xs font-semibold text-hotpink">{credError}</span>}
                </Field>
                <a
                  href="https://portal.goshippo.com/api-config/api"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-hotpink hover:underline"
                >
                  Open Shippo API keys <ExternalLink size={12} />
                </a>
              </Section>

              <Section
                title="Ship-from address"
                caption="Where your packages ship from. This stays the same for every order — set it once here rather than per order."
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Name">
                    <input
                      value={fromAddr.name}
                      onChange={(e) => setFromAddr({ ...fromAddr, name: e.target.value })}
                      placeholder="Your name or business contact"
                      className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-hotpink"
                      data-testid="shippo-from-name-input"
                    />
                  </Field>
                  <Field label="Company (optional)">
                    <input
                      value={fromAddr.company}
                      onChange={(e) => setFromAddr({ ...fromAddr, company: e.target.value })}
                      placeholder="Business name"
                      className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-hotpink"
                    />
                  </Field>
                </div>

                <Field label="Street address">
                  <input
                    value={fromAddr.street1}
                    onChange={(e) => setFromAddr({ ...fromAddr, street1: e.target.value })}
                    placeholder="123 Main St"
                    className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-hotpink"
                    data-testid="shippo-from-street1-input"
                  />
                </Field>
                <Field label="Apartment, suite, etc. (optional)">
                  <input
                    value={fromAddr.street2}
                    onChange={(e) => setFromAddr({ ...fromAddr, street2: e.target.value })}
                    className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-hotpink"
                  />
                </Field>

                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="City">
                    <input
                      value={fromAddr.city}
                      onChange={(e) => setFromAddr({ ...fromAddr, city: e.target.value })}
                      className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-hotpink"
                      data-testid="shippo-from-city-input"
                    />
                  </Field>
                  <Field label="State / Province">
                    <input
                      value={fromAddr.state}
                      onChange={(e) => setFromAddr({ ...fromAddr, state: e.target.value })}
                      className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-hotpink"
                    />
                  </Field>
                  <Field label="Postal code">
                    <input
                      value={fromAddr.zip}
                      onChange={(e) => {
                        setFromAddr({ ...fromAddr, zip: e.target.value });
                        if (zipError) setZipError("");
                      }}
                      className={`w-full rounded-2xl border bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-hotpink ${
                        zipError ? "border-hotpink" : "border-ink/15"
                      }`}
                      data-testid="shippo-from-zip-input"
                    />
                    {zipError && <span className="mt-1 block text-xs font-semibold text-hotpink">{zipError}</span>}
                  </Field>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Country">
                    <select
                      value={fromAddr.country}
                      onChange={(e) => {
                        setFromAddr({ ...fromAddr, country: e.target.value });
                        if (zipError) setZipError("");
                      }}
                      className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-hotpink"
                      data-testid="shippo-from-country-select"
                    >
                      {COUNTRIES.map(([code, name]) => (
                        <option key={code} value={code}>{name}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Phone *">
                    <input
                      value={fromAddr.phone}
                      onChange={(e) => setFromAddr({ ...fromAddr, phone: e.target.value })}
                      placeholder="Required — DHL Express/USPS need this for pickups"
                      className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-hotpink"
                    />
                  </Field>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Email *">
                    <input
                      type="email"
                      value={fromAddr.email}
                      onChange={(e) => setFromAddr({ ...fromAddr, email: e.target.value })}
                      placeholder="Required — DHL Express/USPS need this for pickups"
                      className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-hotpink"
                    />
                  </Field>
                </div>
              </Section>
            </>
          )}

          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={saving}
              data-testid="save-courier-settings"
              className="inline-flex items-center gap-2 rounded-full bg-hotpink px-6 py-3 text-sm font-bold text-white transition-transform hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save courier settings"}
            </button>
            {saved && (
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600">
                <Check size={16} /> Saved
              </span>
            )}
          </div>
        </form>

        <div className="mt-10 rounded-3xl border border-ink/10 bg-white p-6 md:p-8">
          <h2 className="flex items-center gap-2 text-lg font-bold text-ink">
            <Truck size={18} className="text-hotpink" /> Good to know
          </h2>
          <ul className="mt-3 space-y-2 text-sm text-ink/60">
            <li>• Digital-only orders are never sent to a courier — nothing to ship.</li>
            <li>• If a product's weight isn't set in your Catalog yet, we estimate 500g per item so shipments still go through — add real weights in Catalog for more accurate courier rates.</li>
            <li>• Package dimensions default to 10×10×10cm per product unless you set your own in Catalog — this affects rate accuracy for both providers.</li>
            {formProvider === "shippo" && (
              <li>• Shipping outside your ship-from country requires customs info (item description, HS code, value) — add these per product in Catalog before shipping internationally.</li>
            )}
            <li>• If auto-shipment ever fails for an order, you'll get a notification and can still ship it manually from Orders, exactly like before.</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

// -------------------- Reusable subcomponents --------------------------------
function Section({ title, caption, children }) {
  return (
    <div className="rounded-3xl border border-ink/10 bg-white p-6 md:p-8">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-ink">{title}</h2>
        {caption && <p className="mt-1 whitespace-pre-line text-sm text-ink/50">{caption}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-baseline justify-between text-xs font-semibold uppercase tracking-wider text-ink/70">
        <span>{label}</span>
        {hint && <span className="text-[10px] font-medium normal-case text-ink/40">{hint}</span>}
      </span>
      {children}
    </label>
  );
}

function ProviderTab({ active, connected, icon, label, sub, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`provider-tab-${label.toLowerCase()}`}
      className={`flex items-center gap-3 rounded-2xl border p-4 text-left transition-colors ${
        active ? "border-hotpink bg-white" : "border-ink/10 bg-white/60 hover:bg-white"
      }`}
    >
      <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${active ? "bg-hotpink text-white" : "bg-ink/5 text-ink/50"}`}>
        {icon}
      </span>
      <span>
        <span className="flex items-center gap-1.5 text-sm font-bold text-ink">
          {label}
          {connected && <ShieldCheck size={13} className="text-emerald-600" />}
        </span>
        <span className="block text-xs text-ink/50">{sub}</span>
      </span>
    </button>
  );
}
